import { useMemo } from 'react';
import { usePref } from '@taki/ui';
import type { ShellAppEntry } from '../types.js';

/**
 * [MENU DEV] Masquage local des apps : la cle de pref, sa resolution, son hook.
 *
 * BUT : pendant une demo client, ne montrer que les 2 ou 3 apps pertinentes.
 * Ce n'est PAS une desinstallation : rien n'est supprime, la preference vit
 * dans le localStorage de CE poste, et "Tout afficher" la remet a zero.
 *
 * REGLE QUI TIENT TOUT LE LOT : seules les surfaces qui AFFICHENT une liste
 * d'apps passent par `useVisibleAppEntries` (dock, menu Home, barre d'onglets
 * du hub Parametres). Celles qui RESOLVENT ou qui ECRIVENT gardent la liste
 * complete, et il y en a deux :
 *   - la resolution de route (TakiShell) : filtrer ferait tomber l'app ouverte
 *     sur "App introuvable" au moment meme ou on la masque depuis l'overlay
 *     pose au-dessus d'elle ;
 *   - le panneau Dock du hub Parametres : il REECRIT les prefs `dock.apps` et
 *     `dock.shortcuts` a partir de la liste recue, donc une liste filtree
 *     effacerait l'epinglage des apps masquees. Le masquage cache, il ne
 *     detruit jamais une autre preference.
 *
 * CE MASQUAGE EST COSMETIQUE, EN AUCUN CAS UN CONTROLE D'ACCES. Une app
 * masquee reste montable par son URL, par l'historique et par pickApp : c'est
 * voulu. La vraie securite, ce sont les Security Rules (AGENTS.md regle 6) ;
 * l'interface ne fait que masquer.
 *
 * POURQUOI CE MODULE VIT DANS dev/ ET MEURT AVEC LE MENU DEV : l'ecran DEV est
 * la SEULE interface capable de defaire un masquage. Un module permanent
 * laisserait un jour une preference active sans aucun ecran pour la remettre a
 * zero, c'est-a-dire un shell vide sans porte de sortie.
 *
 * Suppression : voir packages/shell/src/dev/README.md.
 */

/**
 * Cle de pref, convention <domaine>.<sujet> du repo (dock.apps,
 * app.<id>.filters). Le domaine n'est PAS le dock : le masquage porte aussi le
 * menu Home et les onglets du hub Parametres.
 */
export const HIDDEN_APPS_PREF_KEY = 'apps.hidden';

/**
 * L'ETAT NORMAL D'UN POSTE NEUF : RIEN N'EST MASQUE.
 *
 * Deux etats, pas trois. Pref ABSENTE ou ILLISIBLE = aucun masquage, donc
 * TOUTE app installee s'affiche, la plus neuve comprise. Pref PRESENTE = elle
 * fait foi, telle quelle, y compris vide (`[]`, l'utilisateur a demande "tout
 * afficher", ce qui donne exactement le meme resultat qu'une pref absente,
 * et c'est tant mieux : l'etat par defaut n'est plus une exception a expliquer).
 *
 * CE QUE CETTE LIGNE REMPLACE, ET POURQUOI ELLE A ETE UN PIEGE. Il y avait ici
 * une liste blanche, `DEFAULT_VISIBLE_APP_IDS`, le "bureau d'un poste neuf" :
 * pref absente valait "ces trois ou quatre apps, pas les autres". Consequence
 * mecanique : une app que le Studio venait de creer n'y figurait pas, donc elle
 * naissait MASQUEE, montee, routee, fonctionnelle et invisible. Le symptome
 * ("mon app n'existe pas") ne ressemblait pas a sa cause ("une pref de demo la
 * masque"). Le contournement etait de faire ECRIRE la liste par le moteur de
 * creation d'app, dans le meme commit que le scaffold, plus un quatrieme
 * fichier a declarer a la main pour qui construit sans le moteur : un patch de
 * fichier partage a chaque app, et le seul des quatre a ne rien casser en CI,
 * donc le seul qu'on oublie.
 *
 * Le vrai defaut n'etait pas l'oubli, c'etait le defaut lui-meme. Une demo se
 * PREPARE (on decoche ce qu'on ne montre pas, deux clics dans le menu DEV, et
 * la pref est alors ecrite), alors qu'un poste neuf se DECOUVRE : c'est lui qui
 * doit tout montrer. La convention du depot le disait depuis toujours pour
 * toutes les autres prefs, cle absente = comportement standard ; c'est ce
 * module qui en etait sorti.
 *
 * Le garde teste la FORME, pas seulement la nullite : la pref vient du
 * localStorage, donc d'un JSON qui a pu etre edite a la main. Sur une valeur ni
 * nulle ni tableau (`{}`, `123`), un `new Set(...)` direct jetterait "not
 * iterable" depuis le composant racine du routeur, c'est-a-dire un shell
 * entierement blanc, sans aucune interface pour defaire la pref.
 */
export function hiddenIdSet(hidden: readonly string[] | null): Set<string> {
  return new Set<string>(Array.isArray(hidden) ? hidden : []);
}

/**
 * Liste des apps a afficher, la pref etant la liste des ids MASQUES.
 *
 * ON STOCKE LES DEVIATIONS, JAMAIS LES VISIBLES : cle absente = visible, et il
 * n'y a plus qu'une seule phrase a retenir.
 *
 * Deux consequences voulues : une pref corrompue montre TOUT (jamais un shell
 * vide), et l'id d'une app desinstallee est simplement ignore, sans purge.
 *
 * Rien de masque : on rend `entries` TEL QUEL (meme reference), pour ne pas
 * invalider les useMemo des appelants a chaque rendu.
 */
export function resolveVisibleApps(
  hidden: readonly string[] | null,
  entries: readonly ShellAppEntry[],
): readonly ShellAppEntry[] {
  const hiddenSet = hiddenIdSet(hidden);
  if (hiddenSet.size === 0) return entries;
  return entries.filter((entry) => !hiddenSet.has(entry.id));
}

/**
 * Bascule le masquage d'une app et rend la nouvelle pref.
 *
 * Part de la pref TELLE QU'ELLE EST, une pref absente ou illisible valant la
 * liste vide : l'etat affiche et l'etat stocke ne peuvent plus diverger ici,
 * puisque "absent" et "rien de masque" sont desormais le meme etat.
 *
 * Ne purge JAMAIS les ids inconnus d'une pref DEJA ecrite : une app
 * desinstallee puis reinstallee retrouve son etat, et une pref ecrite depuis un
 * autre jeu d'apps n'est pas silencieusement amputee.
 */
export function toggleAppHidden(hidden: readonly string[] | null, id: string): string[] {
  const current: readonly string[] = Array.isArray(hidden) ? hidden : [];
  return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
}

/**
 * Le hook que consomment les surfaces d'affichage.
 *
 * `import.meta.env.DEV` est ecrit ICI, LITTERALEMENT, et nulle part ailleurs
 * pour le masquage : une seule source de verite. NE JAMAIS le remplacer par un
 * helper isDev(), une variable intermediaire ou un optional chaining : c'est
 * cette forme textuelle exacte que Vite remplace par une constante au build, et
 * c'est ce remplacement qui permet a esbuild de supprimer la branche.
 *
 * CE GARDE RESTE UTILE MEME MAINTENANT QUE LE DEFAUT NE MASQUE PLUS RIEN. Un
 * build de production sans lui laisserait un localStorage herite d'une demo
 * masquer des apps installees chez un client, sans lui offrir le menu DEV pour
 * le defaire. Ce que le client voit se decide dans `installedApps`, jamais ici.
 *
 * Honnetement : en production le corps se reduit a "return entries" plus une
 * lecture localStorage au montage (usePref est appele sans condition, regle des
 * hooks). Zero interface, zero appel reseau, zero comportement, mais ce n'est
 * pas litteralement zero octet.
 */
export function useVisibleAppEntries(
  entries: readonly ShellAppEntry[],
): readonly ShellAppEntry[] {
  const [hidden] = usePref<string[] | null>(HIDDEN_APPS_PREF_KEY, null);
  return useMemo(
    () => (import.meta.env.DEV ? resolveVisibleApps(hidden, entries) : entries),
    [hidden, entries],
  );
}
